import pick from 'lodash/pick';
import uniq from 'lodash/uniq';

import ObjectUtils from '@spothero/utils/object';
import {
    OBJECT_MONTHLY_FACILITY_SUMMARY,
    OBJECT_MONTHLY_RATE,
    OBJECT_MONTHLY_FACILITY,
} from 'proptypes';
import {
    buildCheckoutURL,
    buildImageTemplate,
    buildUniqueRateAmenities,
    formatAmenitySlug,
    formatDateTime,
    formatDistance,
    formatHoursOfOperation,
} from '../utils';

export function formatMonthlyRate({
    spotId,
    facilitySlug,
    quote: {order, advertisedPrice},
    additionalRateDetails,
    eventId,
    timezone,
    rebookId,
}) {
    // only keep properties that have corresponding rate type (transient, monthly, etc) prop types
    // and combine them with the additional rate details for that rate type as well as any additional
    // properties that rate type needs which are derived from other data not in the API
    const baseRate = order[0];
    const formattedRate = pick(
        {
            ...additionalRateDetails,
            ...baseRate,
            starts: formatDateTime(baseRate.starts, timezone),
            ends: formatDateTime(baseRate.ends, timezone),
            priceBreakdown: baseRate.items,
            amenities: additionalRateDetails.amenities.map(formatAmenitySlug),
            fullUrl: buildCheckoutURL({
                spotId,
                facilitySlug,
                rateId: baseRate.rateId,
                rateType: 'monthly',
                starts: baseRate.starts,
                ends: baseRate.ends,
                eventId,
                timezone,
                rebookId,
            }),
            monthlyReservationType: additionalRateDetails.reservationType,
        },
        Object.keys(OBJECT_MONTHLY_RATE)
    );

    return {
        ...formattedRate,
        advertisedPrice,
    };
}

/**
 * Formats data for a single monthly facility
 *
 * @function formatMonthlyFacility
 * @see {@link https://api.spothero.com/v2/docs#operation/searchMonthlyFacilityParking|Documentation}
 * @param {object} data - The untransformed data from the API for a single facility
 * @param {object} formatOptions - Additional options that don't get passed to the endpoint but are used for data transformation
 * @param {string} formatOptions.timezone - The timezone of the nearest city, used for formatting date/time
 * @param {string} formatOptions.rebookId - The ID of the original reservation that we are rebooking for
 * @param {boolean} formatOptions.isFacilitySummary - If true, will return the shortened version of the facility data (e.g. to display on spot cards)
 * @returns {object} The formatted transient facility data.
 */
export function formatMonthlyFacility(data, formatOptions = {}) {
    const {timezone, rebookId, isFacilitySummary} = formatOptions;
    const dataFormat = isFacilitySummary
        ? OBJECT_MONTHLY_FACILITY_SUMMARY
        : OBJECT_MONTHLY_FACILITY;
    const facilityData = ObjectUtils.camelizeKeys(data);
    const {
        availability,
        distance,
        facility: {common, monthly},
        rates: ratesResponse,
    } = facilityData;

    const {id: spotId, slug: facilitySlug, hoursOfOperation} = common;
    // only keep properties that have corresponding monthly rate prop types
    const rates = ratesResponse.map(({quote, monthly: additionalRateDetails}) =>
        formatMonthlyRate({
            spotId,
            facilitySlug,
            quote,
            additionalRateDetails,
            rateType: 'monthly',
            timezone,
            rebookId,
        })
    );

    // consolidate reservationTypes found across all rate options
    const allMonthlyReservationTypes = uniq(
        rates.map(rate => rate.monthlyReservationType)
    );

    // only keep properties that have corresponding monthly facility prop types
    return pick(
        {
            isV2: true,
            ...common,
            ...monthly,
            images: common.images.map(buildImageTemplate),
            spotId: common.id, // spotId is relied on in a lot of places over id
            availability,
            distance: formatDistance(distance),
            rates,
            selectedRate: rates[0],
            allAmenities: buildUniqueRateAmenities(rates),
            allMonthlyReservationTypes,
            hoursOfOperation: formatHoursOfOperation(hoursOfOperation),
        },
        Object.keys(dataFormat)
    );
}

/**
 * Formats data for multiple monthly facilities
 *
 * @function formatMonthlySearchResponse
 * @see {@link https://api.spothero.com/v2/docs#operation/searchMonthlyParking|Documentation}
 * @param {object} searchResults - Facility data returned from the search API for a multiple facilities
 * @param {object} formatOptions - Additional options that don't get passed to the endpoint but are used for data transformation
 * @returns {Array} The array of monthly facilities, with data transformation applied.
 */
export function formatMonthlySearchResponse(searchResults, formatOptions = {}) {
    const formattedResults = searchResults.map(result =>
        formatMonthlyFacility(result, {
            ...formatOptions,
            isFacilitySummary: true,
        })
    );

    return formattedResults;
}

/**
 * Formats data for a single monthly facility
 *
 * @function formatMonthlySpotDetailsResponse
 * @see {@link https://api.spothero.com/v2/docs#operation/searchMonthlyFacilityParking|Documentation}
 * @param {object} facilityData - All the data returned from the search API for a single facility
 * @param {object} formatOptions - Additional options that don't get passed to the endpoint but are used for data transformation
 * @returns {object} The monthly facility, with data transformation applied.
 */
export function formatMonthlySpotDetailsResponse(facilityData, formatOptions) {
    return formatMonthlyFacility(facilityData, {
        ...formatOptions,
        isFacilitySummary: false,
    });
}
